import { Icon } from "./components/Icon"
import { BookingCalendar } from "./components/BookingCalendar"
import { AvailabilityContinueLabel, ButtonSpinner } from "./components/StatusMessages"
import { PayReserveLabel } from "./components/PayReserveLabel"
import type { CheckoutController } from "./useCheckoutController"

export function getCheckoutPresentation(checkout: CheckoutController) {
    const {
        range,
        resetDates,
        isMobile,
        product,
        choice,
        calendarReady,
        calendarRefreshing,
        calendarSnapshot,
        deliveryFeePence,
        calendarRangeError,
        phase,
        availability,
        stripeReady,
        paymentComplete,
        detailsComplete,
        differentBilling,
        billingComplete,
        addressLookupPhase,
        manualDeliveryEntry,
        walletAvailable,
        quote,
        quantity,
        addonTotalPence,
        selectedAddonIds,
        journeyStep,
        bookingPane,
        continueFromSelection,
        continueFromAddons,
        confirmPayment,
        restoredCheckoutPhase,
        setRestoredCheckoutRetry,
        calendarPhase,
        calendarError,
        refreshCalendar,
        clientSecret,
        error,
    } = checkout
    const calendar = <BookingCalendar {...checkout} />
    const resetDatesButton = (isMobile || !(range?.from && range?.to)) && <button type="button" className="ah-reset-dates" onClick={resetDates} disabled={!range?.from && !range?.to}><Icon name="x" /><span>Reset dates</span></button>
    const pricePromise = product?.pricing?.billing_policy?.short_hire_cap === "standard_week" && <div className="ah-calendar-price-promise">
        <Icon name="zap" /><p><strong>Our price promise</strong><span>Never pay more than the weekly price for shorter hires.</span></p>
    </div>
    const bookingInputsReady = Boolean(product && choice && calendarReady && !calendarRangeError)
    const bookingDisabled = phase === "checking" || phase === "initialising" || !bookingInputsReady || !availability?.available
    const addonsDisabled = phase === "checking" || phase === "initialising" || !bookingInputsReady || !availability?.available
    const checkoutPreparing = phase === "initialising" || Boolean(clientSecret && !stripeReady && !error)
    const walletDisabled = checkoutPreparing || !quote || !calendarReady || Boolean(calendarRangeError) || phase === "paying" || phase === "confirming" || !stripeReady || !detailsComplete || (addressLookupPhase === "loading" && !manualDeliveryEntry)
    const payDisabled = walletDisabled || !paymentComplete || (differentBilling && !billingComplete)
    const walletSectionClass = walletAvailable === false
        ? "ah-wallet-section is-hidden"
        : walletAvailable === null
            ? "ah-wallet-section is-checking"
            : "ah-wallet-section"
    const quotedDeliveryPence = quote?.lineItems.find((item) => item.key === "delivery")?.amountPence ?? 0
    const hireAmountPence = quote?.lineItems.find((item) => item.key === "hire")?.amountPence
        ?? (choice ? choice.pricePence * quantity : 0)
    const mobileDeliveryPence = quote ? quotedDeliveryPence : deliveryFeePence ?? null
    const mobileAmountPence = quote?.totalPence
        ?? (choice ? hireAmountPence + addonTotalPence + (mobileDeliveryPence ?? 0) : 0)
    const addonContinueLabel = phase === "initialising" ? <ButtonSpinner /> : selectedAddonIds.length === 0 ? "Continue without add-ons" : "Continue"
    const selectionChecking = journeyStep === "booking" && bookingPane === "selection"
        && (phase === "checking" || phase === "initialising" || Boolean(choice && calendarRefreshing && calendarSnapshot?.quantity !== quantity))
    const selectionContinueLabel = <AvailabilityContinueLabel checking={selectionChecking} />
    const mobileLabel = journeyStep === "booking"
        ? bookingPane === "addons" ? addonContinueLabel : selectionContinueLabel
        : checkoutPreparing ? <ButtonSpinner /> : <PayReserveLabel processing={phase === "paying"} />
    const mobileAction = journeyStep === "booking"
        ? bookingPane === "selection" ? continueFromSelection : continueFromAddons
        : () => confirmPayment()
    const mobileDisabled = journeyStep === "booking"
        ? bookingPane === "selection" ? bookingDisabled : addonsDisabled
        : payDisabled
    const calendarFeedback = !calendarReady && (
        restoredCheckoutPhase === "error"
            ? <div className="ah-alert ah-error ah-calendar-feedback" role="alert"><span>Your previous checkout could not be checked. Try again.</span><button type="button" className="ah-secondary-button" onClick={() => setRestoredCheckoutRetry((value) => value + 1)}>Retry checkout</button></div>
            : restoredCheckoutPhase === "loading"
                ? <div className="ah-inline-status ah-calendar-feedback" role="status"><span />Checking your previous checkout…</div>
                : calendarPhase === "error"
                    ? <div className="ah-alert ah-error ah-calendar-feedback" role="alert"><span>{calendarError}</span><button type="button" className="ah-secondary-button" onClick={() => void refreshCalendar()}>Retry dates</button></div>
                    : <span className="ah-visually-hidden" role="status">Updating delivery and collection dates…</span>
    )
    return { calendar, resetDatesButton, pricePromise, bookingInputsReady, bookingDisabled, addonsDisabled, payDisabled, walletDisabled, walletSectionClass, quotedDeliveryPence, hireAmountPence, mobileAmountPence, mobileDeliveryPence, addonContinueLabel, selectionChecking, selectionContinueLabel, mobileLabel, mobileAction, mobileDisabled, calendarFeedback, checkoutPreparing }
}
